/**
 * North Carolina Home Care Agency State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary Regulation: 10A NCAC 13J (Licensing of Home Care Agencies)
 * - Statutory Authority: NCGS 131E-135 through 131E-142 (Home Care Agency Licensure Act)
 * - Criminal Background Checks: NCGS 131E-265
 * - Health Care Personnel Registry: NCGS 131E-256
 * - Nurse Aide Registry: NCGS 131E-255
 * 
 * REGULATORY BODY:
 * NC Division of Health Service Regulation (DHSR)
 * Acute and Home Care Licensure and Certification Section
 * Phone: (919) 855-4620
 * Website: https://info.ncdhhs.gov/dhsr/ahc/homecare.html
 * 
 * KEY VERIFIED REQUIREMENTS (December 2024):
 * - License: Annual (1 year) per 10A NCAC 13J .0903(b)
 * - Client record retention: 5 years from discharge per .1401(f)
 * - Personnel record retention: 1 year after termination per .1003(f)
 * - Statistical record retention: 3 years per .1002(c)
 * - Supervisory visit: Every 90 days + annually during care per .1110(c)
 * - Plan of care review: Every 90 days per .1202(a)
 * - Plan of correction: 10 working days per .0904(f)
 * - Ownership/name change notice: 30 days before per .0903(f)
 * - New service category notice: 30 days before per .0903(g)
 * - In-home aide service area: County + contiguous counties OR 90 min drive per .1107(h)
 * 
 * CORRECT 10A NCAC 13J STRUCTURE:
 * Section .0900 - General (Definitions, License, Application, Inspections)
 * Section .1000 - Administration (Management, Records, Personnel, Evaluation, Client Rights)
 * Section .1100 - Scope of Services (Nursing, PT, OT, ST, Social Work, In-Home Aide, etc.)
 * Section .1200 - Case Review and Plan of Care
 * Section .1300 - Pharmaceuticals and Medical Treatment Orders
 * Section .1400 - Service Records
 * Section .1500 - Companion, Sitter, and Respite Services
 * 
 * Last Verified: December 2024 against current 10A NCAC 13J
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const NORTHCAROLINA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'NC-A1',
    itemId: 'A1',
    stateCode: 'NC',
    title: 'Home Care Agency License Status',
    description: 'North Carolina requires all home care agencies to be licensed by DHSR under 10A NCAC 13J',
    severity: 'high',
    regulationCode: '10A NCAC 13J .0902-.0903',
    regulationName: 'Home Care Agency Licensure Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Care Agency license from NC DHSR?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 10A NCAC 13J .0903(b): Licensure shall be for a period of one year.',
      },
      {
        id: 'q2',
        question: 'What is your home care agency license expiration date?',
        inputType: 'date',
        helpText: 'License expires at midnight on the expiration date and is renewable upon application.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your NC DHSR Home Care Agency license is current per 10A NCAC 13J .0902-.0903.',
      atRisk: 'Your license expires within 60 days. Submit renewal application promptly.',
      nonCompliant: 'Operating without a valid license violates NCGS 131E-138 and may result in penalties per NCGS 131E-142.',
      unknown: 'Verify your license status with NC DHSR at (919) 855-4620.',
    },
    stateNotes: [
      'License valid for 1 year per .0903(b)',
      'License must be posted in prominent location per .0903(c)',
      'License not transferable per .0903(d)',
      'New applicants without prior experience must complete DHSR-approved training per .0903(a)',
    ],
  },

  {
    id: 'NC-A2',
    itemId: 'A2',
    stateCode: 'NC',
    title: 'Agency Director Qualifications',
    description: 'North Carolina requires qualified agency director per 10A NCAC 13J .1001(b)',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1001(b)',
    regulationName: 'Agency Director Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency director meet ONE of the 10A NCAC 13J .1001(b) qualification requirements?',
        inputType: 'select',
        options: [
          { value: 'practitioner', label: 'Health care practitioner per NCGS 90-640(a)' },
          { value: '2yr_exp', label: '2+ years supervisory/management in home care or licensed provider' },
          { value: 'degree_1yr', label: 'Bachelor\'s in health/business/public admin + 1 year supervisory experience' },
          { value: 'grandfathered', label: 'Acting as director prior to October 1, 2006' },
          { value: 'none', label: 'Does not meet any requirement' },
        ],
        required: true,
        helpText: 'Per 10A NCAC 13J .1001(b): Agency director must meet one of the listed qualifications.',
      },
      {
        id: 'q2',
        question: 'Do agency policies define the order of authority in the director\'s absence?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per 10A NCAC 13J .1001(e).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['practitioner', '2yr_exp', 'degree_1yr', 'grandfathered'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency director meets 10A NCAC 13J .1001(b) qualification requirements.',
      atRisk: 'Define order of authority for director absence in agency policies per .1001(e).',
      nonCompliant: 'Agency director does not meet qualification requirements.',
      unknown: 'Verify agency director qualifications.',
    },
    stateNotes: [
      'Director vacancy: Notify DHSR within 5 working days per .1001(e)',
      'Written documentation of director responsibilities required per .1001(d)',
      'Director may also serve as service supervisor per .1001(c)',
    ],
  },

  {
    id: 'NC-A3',
    itemId: 'A3',
    stateCode: 'NC',
    title: 'Ownership and Change Notification',
    description: 'Ownership and other changes must be reported to DHSR per 10A NCAC 13J .0903(f)-(h)',
    severity: 'medium',
    regulationCode: '10A NCAC 13J .0903(f)-(h)',
    regulationName: 'Notification of Changes',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Is your ownership and agency information current with DHSR?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, all information is current' },
          { value: 'pending', label: 'Changes pending notification to DHSR' },
          { value: 'outdated', label: 'No, information is outdated' },
          { value: 'unsure', label: 'Not sure if current' },
        ],
        required: true,
        helpText: 'Per .0903(f): Notify DHSR in writing at least 30 days before ownership or name changes.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'current', result: 'COMPLIANT' },
      { condition: 'includes', value: ['pending', 'unsure'], result: 'AT_RISK' },
      { condition: 'equals', value: 'outdated', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency information is current with DHSR.',
      atRisk: 'Notify DHSR 30 days before any ownership/name changes per .0903(f).',
      nonCompliant: 'Failure to notify DHSR of changes violates 10A NCAC 13J .0903.',
      unknown: 'Verify your agency information is current with DHSR.',
    },
    stateNotes: [
      'Ownership/name change: 30 days advance written notice per .0903(f)',
      'New service category: 30 days advance written notice per .0903(g)',
      'Service discontinued 6+ months: Notify DHSR in writing per .0903(h)',
      'Geographic area expansion: 30 days advance written notice per .1001(g)',
    ],
  },

  {
    id: 'NC-A4',
    itemId: 'A4',
    stateCode: 'NC',
    title: 'Written Policies - Agency Management',
    description: 'North Carolina requires specific written policies per 10A NCAC 13J .1001(a)',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1001(a)',
    regulationName: 'Agency Management Policies',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Which required policies does your agency have documented per 10A NCAC 13J .1001(a)?',
        inputType: 'checklist',
        options: [
          { value: 'scope', label: 'Scope of services offered - .1001(a)(1)' },
          { value: 'admission', label: 'Admission and discharge policies - .1001(a)(2)' },
          { value: 'supervision', label: 'Supervision of personnel - .1001(a)(3)' },
          { value: 'poc', label: 'Development and updates to plan of care - .1001(a)(4)' },
          { value: 'emergency', label: 'Management of emergency care situations - .1001(a)(5)' },
          { value: 'records_return', label: 'Time frame for service records return - .1001(a)(6)' },
          { value: 'qualifications', label: 'Personnel qualifications - .1001(a)(7)' },
          { value: 'org_chart', label: 'Organizational chart - .1001(a)(8)' },
          { value: 'evaluation', label: 'Program evaluation - .1001(a)(9)' },
          { value: 'confidentiality', label: 'Employee and client confidentiality - .1001(a)(10)' },
          { value: 'coordination', label: 'Coordination and referral to other agencies - .1001(a)(11)' },
        ],
        required: true,
        helpText: 'All listed policies are required per 10A NCAC 13J .1001(a).',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 11, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 8, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency management policies meet 10A NCAC 13J .1001(a) requirements.',
      atRisk: 'Some required policies are missing.',
      nonCompliant: 'Multiple required policies are missing.',
      unknown: 'Review policies against 10A NCAC 13J .1001(a) requirements.',
    },
    stateNotes: [
      'All 11 policy areas required per .1001(a)',
      'Policies must be available for DHSR inspection',
      'Governing body or designee establishes and implements policies',
    ],
  },

  {
    id: 'NC-A5',
    itemId: 'A5',
    stateCode: 'NC',
    title: 'Quality Assurance / Program Evaluation',
    description: 'North Carolina requires annual comprehensive evaluation per 10A NCAC 13J .1004',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1004',
    regulationName: 'Evaluation Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency conduct annual comprehensive evaluation per 10A NCAC 13J .1004?',
        inputType: 'checklist',
        options: [
          { value: 'annual', label: 'Annual comprehensive evaluation of total operation - .1004(a)' },
          { value: 'quality_review', label: 'Review of service quality with problem resolution - .1004(b)' },
          { value: 'policy_review', label: 'Policy and administration review - .1004(c)' },
          { value: 'data_assessment', label: 'Data assessment (clients, visits, outcomes, staffing) - .1004(c)' },
          { value: 'quarterly_records', label: 'Client record review every 90 days - .1004(d)' },
          { value: 'documentation', label: 'Documentation of evaluators, criteria, methods, actions - .1004(e)' },
        ],
        required: true,
        helpText: 'Per 10A NCAC 13J .1004: Governing body or designee conducts annual evaluation.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Program evaluation meets 10A NCAC 13J .1004 requirements.',
      atRisk: 'Program evaluation is incomplete.',
      nonCompliant: 'Program evaluation does not meet requirements.',
      unknown: 'Review evaluation program against .1004 requirements.',
    },
    stateNotes: [
      'Annual comprehensive evaluation required per .1004(a)',
      'Client record review every 90 days per .1004(d)',
      'Review must include representative sample of all services',
      'Document evaluators, criteria, methods, and actions taken',
    ],
  },

  {
    id: 'NC-A6',
    itemId: 'A6',
    stateCode: 'NC',
    title: 'Client Rights and Responsibilities',
    description: 'North Carolina requires client rights notification per 10A NCAC 13J .1007',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1007',
    regulationName: 'Client Rights Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written client rights per 10A NCAC 13J .1007 and NCGS 131E-144.3?',
        inputType: 'checklist',
        options: [
          { value: 'written_advance', label: 'Written notice provided in advance or at initial visit - .1007(a)' },
          { value: 'documentation', label: 'Documentation that clients were informed - .1007(a)' },
          { value: 'statutory_rights', label: 'Rights per NCGS 131E-144.3 included' },
          { value: 'notice_posted', label: 'Required notices posted per NCGS 131E-144.4' },
        ],
        required: true,
        helpText: 'Per .1007(a): Written notice before care or during initial evaluation visit.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client rights documentation meets 10A NCAC 13J .1007 requirements.',
      atRisk: 'Client rights documentation may be incomplete.',
      nonCompliant: 'Client rights not properly provided per .1007.',
      unknown: 'Review client rights procedures.',
    },
    stateNotes: [
      'Written rights before care begins or at initial evaluation',
      'Maintain documentation that all clients informed',
      'DHSR investigates non-compliance per .1007(b)',
    ],
  },

  {
    id: 'NC-A7',
    itemId: 'A7',
    stateCode: 'NC',
    title: 'Client Record Retention',
    description: 'North Carolina requires 5-year client record retention per 10A NCAC 13J .1401(f)',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1401(f)',
    regulationName: 'Service Record Retention',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain client service records after discharge?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer (meets NC requirement)' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Per .1401(f): Client records retained at least 5 years from discharge; minors until 3 years after 18th birthday.',
      },
      {
        id: 'q2',
        question: 'If you cease operations, will you notify DHSR of record storage location?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, have plan in place' },
          { value: 'no', label: 'No plan in place' },
        ],
        helpText: 'Required per .1401(f): Notify DHSR in writing where records will be stored.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client record retention meets NC 5-year requirement per .1401(f).',
      atRisk: 'NC requires 5-year retention. Update your policy.',
      nonCompliant: 'Record retention does not meet NC 5-year requirement.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      '5 years from most recent discharge per .1401(f)',
      'Minors: Until 3 years after 18th birthday',
      'Notify DHSR of storage location if ceasing operations',
      'Records kept confidential and secure per .1401(c)',
    ],
  },

  {
    id: 'NC-A8',
    itemId: 'A8',
    stateCode: 'NC',
    title: 'Survey and Plan of Correction Compliance',
    description: 'North Carolina requires survey compliance per 10A NCAC 13J .0904',
    severity: 'high',
    regulationCode: '10A NCAC 13J .0904',
    regulationName: 'Inspection Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'If deficiencies were cited in your last survey, did you submit POC within 10 working days?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, POC submitted within 10 working days' },
          { value: 'late', label: 'POC submitted late' },
          { value: 'none', label: 'No deficiencies cited' },
          { value: 'na', label: 'Not yet surveyed' },
        ],
        required: true,
        helpText: 'Per .0904(f): Plan of correction due within 10 working days of receipt.',
      },
      {
        id: 'q2',
        question: 'Do you allow immediate access to premises and records during DHSR inspections?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per .0904(e): Agency shall allow immediate access. Failure may result in injunctive relief.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['yes', 'none', 'na'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'late', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Survey compliance satisfactory.',
      atRisk: 'POC must be submitted within 10 working days per .0904(f).',
      nonCompliant: 'Failure to allow DHSR access may result in injunctive relief per .0904(e).',
      unknown: 'Review survey compliance requirements.',
    },
    stateNotes: [
      'POC due within 10 working days per .0904(f)',
      'DHSR responds to POC within 10 working days',
      'Immediate access required per .0904(e)',
      'DHSR may visit clients at home per .0904(g)',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'NC-B1',
    itemId: 'B1',
    stateCode: 'NC',
    title: 'Personnel Record Requirements',
    description: 'North Carolina requires specific personnel file elements per 10A NCAC 13J .1003(f)',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1003(f)',
    regulationName: 'Personnel Records',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Which required elements do you maintain in EVERY employee\'s personnel file per .1003(f)?',
        inputType: 'checklist',
        options: [
          { value: 'application', label: 'Application/resume with verifiable education, training, employment - .1003(f)(1)' },
          { value: 'job_desc', label: 'Job description with employee acknowledgment - .1003(f)(2)' },
          { value: 'references', label: 'Reference checks or employment verification - .1003(f)(3)' },
          { value: 'tb', label: 'TB screening records (hands-on care staff) - .1003(f)(4)' },
          { value: 'hep_b', label: 'Hepatitis B immunization or declination (hands-on care) - .1003(f)(5)' },
          { value: 'pathogen', label: 'Airborne/bloodborne pathogen training with annual updates - .1003(f)(6)' },
          { value: 'evaluations', label: 'Performance evaluations at least annually - .1003(f)(7)' },
          { value: 'credentials', label: 'Credentials verification - .1003(f)(8)' },
          { value: 'competency', label: 'Competency verification records - .1003(f)(9)' },
        ],
        required: true,
        helpText: 'Per .1003(f): All listed elements required. Records maintained at least 1 year after termination.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all required elements per 10A NCAC 13J .1003(f).',
      atRisk: 'Some personnel files missing required documents.',
      nonCompliant: 'Personnel files missing critical documentation.',
      unknown: 'Audit personnel files against .1003(f) requirements.',
    },
    stateNotes: [
      'All 9 elements required per .1003(f)',
      'Retain 1 year after termination per .1003(f)',
      'Records available on premises for DHSR inspection',
      'Evaluations may be confidential per .0905',
    ],
  },

  {
    id: 'NC-B2',
    itemId: 'B2',
    stateCode: 'NC',
    title: 'In-Home Aide Core Competency Verification',
    description: 'In-home aides not on nurse aide registry require RN competency verification per 10A NCAC 13J .1003(g)',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1003(g)',
    regulationName: 'In-Home Aide Competency Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ in-home aides NOT listed on the nurse aide registry?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - all on registry or no in-home aides' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For those aides, has an RN verified competency in all 6 core personal care skills per .1003(g)?',
        inputType: 'checklist',
        options: [
          { value: 'mobility', label: 'Mobility (ambulation, transfers, bed mobility) - .1003(g)(1)' },
          { value: 'bathing', label: 'Bath/Shower assistance - .1003(g)(2)' },
          { value: 'toileting', label: 'Toileting assistance - .1003(g)(3)' },
          { value: 'dressing', label: 'Dressing assistance - .1003(g)(4)' },
          { value: 'eating', label: 'Eating assistance - .1003(g)(5)' },
          { value: 'continence', label: 'Continence needs assistance - .1003(g)(6)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per .1003(g): RN verification required for in-home aides hired after April 1, 2009.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'In-home aide competency verification meets .1003(g) requirements.',
      atRisk: 'Some core competencies may not be RN-verified.',
      nonCompliant: 'In-home aides lacking required RN competency verification.',
      unknown: 'Verify in-home aide competency documentation.',
    },
    stateNotes: [
      'Applies to in-home aides NOT on nurse aide registry',
      'RN must verify 6 core personal care skills',
      'Applies to aides hired after April 1, 2009',
    ],
  },

  {
    id: 'NC-B3',
    itemId: 'B3',
    stateCode: 'NC',
    title: 'Personnel Record Retention',
    description: 'North Carolina requires 1-year personnel record retention after termination per 10A NCAC 13J .1003(f)',
    severity: 'medium',
    regulationCode: '10A NCAC 13J .1003(f)',
    regulationName: 'Personnel Record Retention',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain personnel records after employee termination?',
        inputType: 'select',
        options: [
          { value: '1plus', label: '1 year or longer (meets NC requirement)' },
          { value: 'less1', label: 'Less than 1 year' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Per .1003(f): Personnel records maintained at least 1 year after termination.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '1plus', result: 'COMPLIANT' },
      { condition: 'includes', value: ['less1', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel record retention meets NC 1-year requirement.',
      atRisk: 'Personnel record retention policy needs review.',
      nonCompliant: 'Personnel records not retained 1 year after termination as required.',
      unknown: 'Review personnel record retention requirements.',
    },
    stateNotes: [
      '1 year after termination per .1003(f)',
      'Note: Client records are 5 years per .1401(f)',
      'Statistical records are 3 years per .1002(c)',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'NC-C1',
    itemId: 'C1',
    stateCode: 'NC',
    title: 'Criminal Background Checks (NCGS 131E-265)',
    description: 'North Carolina requires criminal history checks for staff entering patient homes',
    severity: 'high',
    regulationCode: 'NCGS 131E-265',
    regulationName: 'Criminal History Record Check Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'For staff entering patient homes, which background checks do you complete per NCGS 131E-265?',
        inputType: 'checklist',
        options: [
          { value: 'state', label: 'NC State criminal history check (all employees)' },
          { value: 'national', label: 'National criminal history check with fingerprints (if NC resident <5 years)' },
          { value: 'hcpr', label: 'NC Health Care Personnel Registry check (NCGS 131E-256)' },
          { value: 'oig', label: 'OIG/Medicaid Exclusion List verification' },
        ],
        required: true,
        helpText: 'Per NCGS 131E-265(a): Background check required for positions requiring entry into patient home.',
      },
      {
        id: 'q2',
        question: 'Do you submit background check requests within 5 business days of conditional offer?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per NCGS 131E-265(a).',
      },
      {
        id: 'q3',
        question: 'Do employees begin working with patients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
        helpText: 'Per NCGS 131E-265: Employees must not have patient contact until background check results are received.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation - must be evaluated FIRST)
      { condition: 'equals', questionId: 'q3', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if not submitting within 5 days
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 3+ checks AND submit within 5 days AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check procedures meet NCGS 131E-265 requirements.',
      atRisk: 'Background check documentation may be incomplete.',
      nonCompliant: 'VIOLATION: Staff are working before background clearance OR required checks are missing. Per NCGS 131E-265, employees must not have patient contact until background check results are received. Immediately remove uncleared staff from patient care.',
      unknown: 'Audit all personnel files for background check documentation.',
    },
    stateNotes: [
      'State check required for all home-entering staff',
      'National + fingerprints if NC resident less than 5 years',
      'Submit request within 5 business days of conditional offer',
      'Cannot employ anyone who refuses consent',
    ],
  },

  {
    id: 'NC-C2',
    itemId: 'C2',
    stateCode: 'NC',
    title: 'NC Health Care Personnel Registry Check',
    description: 'Must verify all health care personnel against NC HCPR per NCGS 131E-256',
    severity: 'high',
    regulationCode: 'NCGS 131E-256 / 10A NCAC 13J .1003(c)',
    regulationName: 'Health Care Personnel Registry Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://ncnar.ncdhhs.gov/verify_listings1.jsp',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify ALL unlicensed health care personnel against the NC HCPR before hiring?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, 100% verified before hire' },
          { value: 'most', label: 'Most are verified' },
          { value: 'no', label: 'No / not aware of requirement' },
        ],
        required: true,
        helpText: 'Per NCGS 131E-256 and .1003(c): Cannot hire anyone with substantiated finding on HCPR.',
      },
      {
        id: 'q2',
        question: 'Do you document each HCPR verification in business files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per NCGS 131E-256: Must note each incident of access in appropriate business files.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HCPR verification performed and documented for all hires.',
      atRisk: 'HCPR verification may be inconsistent.',
      nonCompliant: 'Failure to verify HCPR before hiring violates NCGS 131E-256.',
      unknown: 'Verify HCPR checking procedures are in place.',
    },
    stateNotes: [
      'Check HCPR before hiring any unlicensed health care personnel',
      'Cannot hire anyone with substantiated finding per .1003(c)',
      'Document each verification in business files',
      'HCPR URL: https://ncnar.ncdhhs.gov/verify_listings1.jsp',
    ],
  },

  {
    id: 'NC-C3',
    itemId: 'C3',
    stateCode: 'NC',
    title: 'OIG/Medicaid Exclusion List Verification',
    description: 'Must verify staff are not on federal exclusion lists',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusion List Requirements',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'How often do you check the OIG exclusion list for employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No / not checking' },
        ],
        required: true,
        helpText: 'OIG recommends monthly verification for Medicare/Medicaid participating providers.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification performed regularly.',
      atRisk: 'Implement monthly OIG verification. At-hire only is insufficient.',
      nonCompliant: 'Employing excluded individuals can result in civil monetary penalties.',
      unknown: 'Verify OIG checking procedures.',
    },
    stateNotes: [
      'OIG recommends monthly verification',
      'Check at hire and monthly thereafter',
      'Document all exclusion checks',
      'Required for Medicare/Medicaid participation',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'NC-D1',
    itemId: 'D1',
    stateCode: 'NC',
    title: 'TB Screening Requirements',
    description: 'North Carolina requires TB screening for hands-on care employees per 10A NCAC 13J .1003(b)',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1003(b)',
    regulationName: 'TB Screening',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL hands-on care employees have baseline TB skin test on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have TB screening documentation' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
        helpText: 'Per .1003(b): Baseline TB skin test required. Positive individuals must demonstrate non-infectious status.',
      },
      {
        id: 'q2',
        question: 'For TB-positive employees, do you have annual verification they are free of TB symptoms?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A - no TB-positive employees' },
        ],
        helpText: 'Per .1003(b): Annual verification from health department, physician, or agency health nurse.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is complete per .1003(b).',
      atRisk: 'Some TB screenings may be missing.',
      nonCompliant: 'Hands-on care staff working without TB screening.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'Baseline TB skin test required for hands-on care employees',
      'Positive result: Must demonstrate non-infectious status before assignment',
      'Previously positive: Annual verification of symptom-free status',
      'Subsequent testing per OSHA standards for at-risk employees',
    ],
  },

  {
    id: 'NC-D2',
    itemId: 'D2',
    stateCode: 'NC',
    title: 'Infection Control Program',
    description: 'Agency must maintain infection control policies per 10A NCAC 13J .1003(a)',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1003(a)',
    regulationName: 'Infection Control Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have written infection control policies per .1003(a)?',
        inputType: 'checklist',
        options: [
          { value: 'communicable', label: 'Infection control and exposure to communicable diseases' },
          { value: 'osha', label: 'Compliance with 29 CFR 1910 (OSHA standards)' },
          { value: 'exposure_plan', label: 'Exposure control plan' },
          { value: 'hand_hygiene', label: 'Hand hygiene procedures' },
          { value: 'ppe', label: 'PPE usage guidelines' },
        ],
        required: true,
        helpText: 'Per .1003(a): Policies must be consistent with 15A NCAC 19A and 29 CFR 1910.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets .1003(a) requirements.',
      atRisk: 'Infection control program is incomplete.',
      nonCompliant: 'No adequate infection control program exists.',
      unknown: 'Review infection control program requirements.',
    },
    stateNotes: [
      'Policies consistent with 15A NCAC 19A (Communicable Disease)',
      'Compliance with 29 CFR 1910 (OSHA)',
      'Include provisions for exposure control plan',
    ],
  },

  // ============================================
  // SECTION E: SUPERVISION & COMPETENCY
  // ============================================
  {
    id: 'NC-E1',
    itemId: 'E1',
    stateCode: 'NC',
    title: 'In-Home Caregiver Supervision',
    description: 'North Carolina requires supervisory visits every 90 days per 10A NCAC 13J .1110(c)',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1110(c)',
    regulationName: 'Supervision Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct supervisory visits to client residences for in-home caregivers per .1110(c)?',
        inputType: 'checklist',
        options: [
          { value: 'every_90', label: 'Supervisory visit every 90 days with or without caregiver present' },
          { value: 'annually_during', label: 'At least annually while caregiver is providing care' },
          { value: 'review_condition', label: 'Review client\'s general condition, progress, and response' },
          { value: 'documented', label: 'Documentation includes date, findings, and signature' },
        ],
        required: true,
        helpText: 'Per .1110(c)-(d): Health care practitioner supervises by visiting every 90 days + annually during care.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'In-home caregiver supervision meets .1110 requirements.',
      atRisk: 'Supervisory visits may not meet frequency requirements.',
      nonCompliant: 'In-home caregivers not receiving required supervision.',
      unknown: 'Review supervision requirements.',
    },
    stateNotes: [
      'Visit every 90 days with or without caregiver present',
      'At least annually while caregiver is providing care',
      'Document date, findings, and signature per .1110(d)',
      'Corrective action documented in employee record per .1110(e)',
      'Supervisor available during service hours per .1110(g)',
    ],
  },

  {
    id: 'NC-E2',
    itemId: 'E2',
    stateCode: 'NC',
    title: 'Competency Verification',
    description: 'Staff must demonstrate competency for assigned tasks per 10A NCAC 13J .1110(a)-(b)',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1110(a)-(b)',
    regulationName: 'Competency Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL in-home caregivers have documented competency verification per .1110?',
        inputType: 'checklist',
        options: [
          { value: 'documented', label: 'Agency documents competency for all assigned tasks' },
          { value: 'demonstration', label: 'Competency includes task demonstration to health care practitioner' },
          { value: 'only_assigned', label: 'Staff only assigned activities for which competent' },
          { value: 'supervision', label: 'Activities performed under authorized supervision' },
        ],
        required: true,
        helpText: 'Per .1110(a)-(b): Competency includes demonstration of tasks to health care practitioner.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Competency verification meets .1110 requirements.',
      atRisk: 'Some competency documentation may be incomplete.',
      nonCompliant: 'Staff working without documented competency verification.',
      unknown: 'Verify competency documentation for all staff.',
    },
    stateNotes: [
      'Document competency for all assigned client care tasks',
      'Competency includes task demonstration',
      'Only assign activities for which staff demonstrated competency',
      'Licensed staff meet occupational licensing board rules per .1110(a)',
    ],
  },

  {
    id: 'NC-E3',
    itemId: 'E3',
    stateCode: 'NC',
    title: 'In-Service Education',
    description: 'Agency must maintain in-service education records per 10A NCAC 13J .1003(d)',
    severity: 'medium',
    regulationCode: '10A NCAC 13J .1003(d)',
    regulationName: 'In-Service Education',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain records of in-service education and attendance per .1003(d)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, complete records maintained' },
          { value: 'some', label: 'Some records, not complete' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
        helpText: 'Per .1003(d): Written policies shall include in-service education. Records shall be maintained.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'In-service education records are complete.',
      atRisk: 'In-service education records may be incomplete.',
      nonCompliant: 'In-service education not being tracked.',
      unknown: 'Review in-service education requirements.',
    },
    stateNotes: [
      'Written policies must include in-service education per .1003(d)',
      'Maintain records of in-service and attendance',
      'Retain per personnel record retention requirements',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'NC-F1',
    itemId: 'F1',
    stateCode: 'NC',
    title: 'Plan of Care Requirements',
    description: 'North Carolina requires written plan of care per 10A NCAC 13J .1202',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1202',
    regulationName: 'Case Review and Plan of Care',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your plan of care process meet 10A NCAC 13J .1202 requirements?',
        inputType: 'checklist',
        options: [
          { value: 'established', label: 'POC established in collaboration with client - .1202(a)' },
          { value: 'incorporated', label: 'POC incorporated in service record - .1202(a)' },
          { value: 'reviewed_90', label: 'POC reviewed by health care practitioner every 90 days - .1202(a)' },
          { value: 'service_type', label: 'Includes type of service(s) and care - .1202(a)(1)' },
          { value: 'frequency', label: 'Includes frequency and duration - .1202(a)(2)' },
          { value: 'restrictions', label: 'Includes activity restrictions - .1202(a)(3)' },
          { value: 'safety', label: 'Includes safety measures - .1202(a)(4)' },
          { value: 'objectives', label: 'Includes service objectives and goals - .1202(a)(5)' },
        ],
        required: true,
        helpText: 'Per .1202(a): POC reviewed every 90 days and revised as needed.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care meet 10A NCAC 13J .1202 requirements.',
      atRisk: 'Plans of care may be incomplete or not reviewed timely.',
      nonCompliant: 'Plans of care do not meet requirements.',
      unknown: 'Review care plan process against .1202 requirements.',
    },
    stateNotes: [
      'Establish POC in collaboration with client',
      'Review every 90 days by health care practitioner',
      'Revise as needed based on client needs',
      'Original and updated orders maintained in record if purged',
      'Notify physician of changes indicating need to alter POC',
    ],
  },

  {
    id: 'NC-F2',
    itemId: 'F2',
    stateCode: 'NC',
    title: 'In-Home Aide Service Requirements',
    description: 'North Carolina has specific in-home aide service requirements per 10A NCAC 13J .1107',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1107',
    regulationName: 'In-Home Aide Services',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do your in-home aide services meet .1107 requirements?',
        inputType: 'checklist',
        options: [
          { value: 'poc_signed', label: 'POC signed and dated by health care practitioner and client - .1107(a)' },
          { value: 'copy_access', label: 'Client has access to POC copy in home - .1107(a)' },
          { value: 'level_documented', label: 'Level of ADL assistance documented in POC - .1107(b)' },
          { value: 'initial_assessment', label: 'Initial assessment conducted in home by practitioner - .1107(d)' },
          { value: 'assessment_before_poc', label: 'Assessment completed before POC development - .1107(e)' },
          { value: 'practitioner_available', label: 'Health care practitioner available for supervision/consultation - .1107(f)' },
        ],
        required: true,
        helpText: 'Per .1107: In-home aide POC requires signatures and in-home assessment.',
      },
      {
        id: 'q2',
        question: 'For clients requiring extensive assistance, are your aides listed on the Nurse Aide Registry?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, all extensive assistance provided by registry-listed aides' },
          { value: 'no', label: 'No / not sure' },
          { value: 'na', label: 'N/A - no extensive assistance clients' },
        ],
        helpText: 'Per .1107(b): Extensive assistance requires registry-listed nurse aide per NCGS 131E-255.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'In-home aide services meet .1107 requirements.',
      atRisk: 'Some in-home aide service requirements may not be met.',
      nonCompliant: 'In-home aide services do not meet .1107 requirements.',
      unknown: 'Review in-home aide service requirements.',
    },
    stateNotes: [
      'POC signed by health care practitioner AND client',
      'Client has access to POC copy in home',
      'Extensive assistance: Nurse Aide Registry listing required',
      'Limited assistance: Registry listing not required',
      'Initial assessment by practitioner in client home',
      'Geographic limit: County + contiguous OR 90 min drive per .1107(h)',
    ],
  },

  {
    id: 'NC-F3',
    itemId: 'F3',
    stateCode: 'NC',
    title: 'Nursing Services Requirements',
    description: 'North Carolina nursing service requirements per 10A NCAC 13J .1102',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1102',
    regulationName: 'Nursing Services',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide nursing services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your nursing services meet .1102 requirements?',
        inputType: 'checklist',
        options: [
          { value: 'rn_supervision', label: 'Services provided by or under RN supervision - .1102(a)' },
          { value: 'nursing_practice', label: 'In accordance with NC Nursing Practice Act - .1102(a)' },
          { value: 'on_call', label: '24/7 on-call nursing services provided - .1102(c)' },
          { value: 'schedules', label: 'Current and 1-year previous on-call schedules retained - .1102(c)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Nursing services meet .1102 requirements.',
      atRisk: 'Some nursing service requirements may not be met.',
      nonCompliant: 'Nursing services do not meet .1102 requirements.',
      unknown: 'Review nursing service requirements.',
    },
    stateNotes: [
      'Services by or under RN supervision',
      '24/7 on-call nursing if providing nursing services',
      'LPN duties delegated by and under RN supervision per .1102(b)',
      'Retain current and 1-year previous on-call schedules',
    ],
  },

  // ============================================
  // SECTION G: SERVICE RECORDS / DOCUMENTATION
  // ============================================
  {
    id: 'NC-G1',
    itemId: 'G1',
    stateCode: 'NC',
    title: 'Client Service Record Requirements',
    description: 'North Carolina requires specific service record content per 10A NCAC 13J .1402',
    severity: 'high',
    regulationCode: '10A NCAC 13J .1402',
    regulationName: 'Content of Record',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do your client service records contain required admission data per .1402(a)(1)?',
        inputType: 'checklist',
        options: [
          { value: 'identification', label: 'Identification data (name, address, DOB, etc.) - (A)' },
          { value: 'rights_signed', label: 'Signed client rights form or delivery documentation - (B)' },
          { value: 'kin', label: 'Names of next of kin, guardian, or family - (C)' },
          { value: 'referral', label: 'Source of referral - (D)' },
          { value: 'home_assessment', label: 'Assessment of home environment - (E)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your client service records contain required service data per .1402(a)(2)?',
        inputType: 'checklist',
        options: [
          { value: 'initial_assessment', label: 'Initial assessment of functional status - (A)' },
          { value: 'problems_goals', label: 'Identification of problems, goals, intervention, client approval - (B)' },
          { value: 'service_record', label: 'Record of all services with date, time, and signature - (C)' },
          { value: 'discharge', label: 'Discharge summary with date and reason - (D)' },
          { value: 'coordination', label: 'Evidence of service coordination - (E)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'countLessThan', questionId: 'q1', value: 3 },
        { condition: 'countLessThan', questionId: 'q2', value: 3 },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client service records contain all required elements per .1402.',
      atRisk: 'Client service records may be missing required elements.',
      nonCompliant: 'Client service records are incomplete.',
      unknown: 'Audit client records against .1402 requirements.',
    },
    stateNotes: [
      'Each page must have client name per .1401(b)',
      'All entries reflect actual date of entry',
      'Late entries noted as addendum/late entry with date',
      'If physician orders required, additional elements per .1402(b)',
    ],
  },

  {
    id: 'NC-G2',
    itemId: 'G2',
    stateCode: 'NC',
    title: 'Service Record Security and Confidentiality',
    description: 'North Carolina requires secure client records per 10A NCAC 13J .1401',
    severity: 'medium',
    regulationCode: '10A NCAC 13J .1401(c)-(d)',
    regulationName: 'Record Security Requirements',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain client record security per .1401(c)-(d)?',
        inputType: 'checklist',
        options: [
          { value: 'confidential', label: 'Originals kept confidential and secure on premises - .1401(c)' },
          { value: 'sign_out', label: 'Sign out log maintained when records removed - .1401(d)' },
          { value: 'return_5days', label: 'Records returned within 5 working days if removed for evaluation - .1401(d)' },
          { value: 'authorized_only', label: 'Only authorized staff or persons authorized by law may remove - .1401(d)' },
          { value: 'copy_available', label: 'Copy readily available to health professionals providing services - .1401(e)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client record security meets .1401 requirements.',
      atRisk: 'Record security procedures may be incomplete.',
      nonCompliant: 'Record security does not meet requirements.',
      unknown: 'Review record security procedures.',
    },
    stateNotes: [
      'Original records on licensed premises unless subpoenaed or for evaluation',
      'Sign out log with name of person, client name, date removed',
      'Return within 5 working days if removed for evaluation',
      'Only authorized staff may remove records',
    ],
  },

  {
    id: 'NC-G3',
    itemId: 'G3',
    stateCode: 'NC',
    title: 'Acceptance of Clients Policy',
    description: 'North Carolina requires written client acceptance policies per 10A NCAC 13J .1101',
    severity: 'medium',
    regulationCode: '10A NCAC 13J .1101',
    regulationName: 'Acceptance of Clients',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do your client acceptance policies include all .1101 requirements?',
        inputType: 'checklist',
        options: [
          { value: 'adequacy', label: 'Adequacy of agency personnel and resources - .1101(1)' },
          { value: 'expectation', label: 'Reasonable expectation needs can be met at home - .1101(2)' },
          { value: 'facilities', label: 'Adequate physical facilities in client home - .1101(3)' },
          { value: 'family', label: 'Availability of family to participate when necessary - .1101(4)' },
          { value: 'scope_info', label: 'Information on scope and geographic area served - .1101(5)' },
          { value: 'notification', label: 'Notification when service cannot be provided timely - .1101(6)' },
          { value: 'advance_notice', label: '48-hour notice for service reduction/termination - .1101(7)' },
          { value: 'referral', label: 'Referral and coordination when unable to serve - .1101(8)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client acceptance policies meet .1101 requirements.',
      atRisk: 'Client acceptance policies may be incomplete.',
      nonCompliant: 'Client acceptance policies do not meet requirements.',
      unknown: 'Review client acceptance policies.',
    },
    stateNotes: [
      'All 8 policy elements required per .1101',
      '48-hour advance notice for reduction/termination (exceptions apply)',
      'Referral to other agencies when unable to serve',
    ],
  },

  {
    id: 'NC-G4',
    itemId: 'G4',
    stateCode: 'NC',
    title: 'Contract Services Arrangements',
    description: 'Contract arrangements must meet 10A NCAC 13J .1111 requirements',
    severity: 'medium',
    regulationCode: '10A NCAC 13J .1111',
    regulationName: 'Arrangements for Services',
    agencyName: 'NC Division of Health Service Regulation',
    agencyAbbr: 'DHSR',
    agencyPhone: '(919) 855-4620',
    agencyUrl: 'https://info.ncdhhs.gov/dhsr/ahc/homecare.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you contract with other agencies or individuals for services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - all services by employees' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do ALL contracts include required elements per .1111(a)?',
        inputType: 'checklist',
        options: [
          { value: 'specific_service', label: 'Specific service to be provided - .1111(a)(1)' },
          { value: 'period', label: 'Period of time contract in effect - .1111(a)(2)' },
          { value: 'availability', label: 'Availability of services - .1111(a)(3)' },
          { value: 'financial', label: 'Financial arrangements - .1111(a)(4)' },
          { value: 'license_verify', label: 'License/registration verification - .1111(a)(5)' },
          { value: 'supervision', label: 'Supervision of contract personnel - .1111(a)(6)' },
          { value: 'same_requirements', label: 'Contractors meet same requirements as agency staff - .1111(a)(7)' },
          { value: 'documentation', label: 'Documentation of services in client record - .1111(a)(8)' },
          { value: 'poc_sharing', label: 'Sharing of assessment and POC data - .1111(a)(9)' },
          { value: 'geographic', label: 'Geographic service area - .1111(a)(10)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contract arrangements meet .1111 requirements.',
      atRisk: 'Contracts may be missing required elements.',
      nonCompliant: 'Contracts do not meet .1111 requirements.',
      unknown: 'Review contracts against .1111 requirements.',
    },
    stateNotes: [
      'Written agreement signed by both parties required',
      'All 10 elements required per .1111(a)',
      'Services per client POC per .1111(b)',
      'Review and update contracts annually per .1111(c)',
      'Subcontracting agency maintains complete home care record per .1111(d)',
    ],
  },
];

export default NORTHCAROLINA_QUESTIONS;